import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../helpers/api';
import { REPORT_GENERATION_MODES } from '../helpers/constants';
import { toSafeApiErrorHtml } from '../helpers/safeHtml';

const useReportGeneration = ({
  endpoints,
  getEntityId,
  getReportId,
  prepareReportData,
  fileInformation,
  onSuccess,
  onValidationError,
  downloadFilePrefix = 'Report',
  buildSaveRequest,
  buildGenerateRequest,
  getApplyWaterMarkFlag
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportGenerationMode, setReportGenerationMode] = useState(null);
  const [generatedFile, setGeneratedFile] = useState(null);

  const onSaveReport = async () => {
    setIsSaving(true);

    try {
      const saveData = prepareReportData();
      const reportId = getReportId();
      const endpoint = endpoints.save(reportId);
      const defaultSaveRequest = {
        requestBody: {
          orderId: getEntityId(),
          ...saveData,
          ...fileInformation,
          skipValidation: true
        },
        contentType: 'application/json',
        stringify: true
      };
      const { requestBody, contentType, stringify } = buildSaveRequest
        ? buildSaveRequest({
            saveData,
            reportId,
            entityId: getEntityId(),
            fileInformation
          })
        : defaultSaveRequest;

      const { error, response } = await makeRESTApiRequest({
        endpoint,
        method: 'PUT',
        requestBody,
        contentType,
        stringify
      });

      if (error) {
        toast.error(error);
        return false;
      }

      toast.success('Report saved successfully');

      if (onSuccess) {
        onSuccess(response);
      }

      return true;
    } catch (err) {
      toast.error(err?.message || 'Failed to save report');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const generateReport = async (mode, appendFiles) => {
    const skipValidation = mode === REPORT_GENERATION_MODES.QUICK_PREVIEW;

    setIsGeneratingReport(true);
    setReportGenerationMode(mode);

    try {
      const reportData = prepareReportData();
      const reportId = getReportId();
      const applyWaterMark = getApplyWaterMarkFlag?.() === true;

      const defaultGenerateRequest = {
        requestBody: {
          _id: reportId,
          orderId: getEntityId(),
          ...fileInformation,
          ...reportData,
          appendFiles,
          skipValidation
        },
        contentType: 'application/json',
        stringify: true
      };
      const { requestBody: builtBody, contentType, stringify } = buildGenerateRequest
        ? buildGenerateRequest({
            reportData,
            reportId,
            entityId: getEntityId(),
            fileInformation,
            appendFiles,
            skipValidation
          })
        : defaultGenerateRequest;

      const requestBody = { ...builtBody, applyWaterMark };

      const { response: blob, error, fieldErrors } = await makeRESTApiRequest({
        endpoint: endpoints.generate,
        method: 'POST',
        requestBody,
        contentType,
        stringify,
        responseType: 'blob'
      });

      if (error) {
        onValidationError?.({ message: error, fieldErrors: fieldErrors || {} });
        toast.error('', { description: <div dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(error) }} /> });
        setReportGenerationMode(null);
        return;
      }

      onValidationError?.({ fieldErrors: {} });

      const blobUrl = URL.createObjectURL(blob);

      setGeneratedFile({
        href: blobUrl,
        filename: `${downloadFilePrefix}_${fileInformation?.propertyAddress?.replace(/ /g, '_')}.pdf`,
        contentType: blob.type,
        size: blob.size,
        isWaterMark: applyWaterMark
      });

      setReportGenerationMode(mode);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to generate report');
      setReportGenerationMode(null);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const onGenerateReport = () => generateReport(REPORT_GENERATION_MODES.PREVIEW_AND_SHARE, true);

  const onQuickPreview = () => generateReport(REPORT_GENERATION_MODES.QUICK_PREVIEW, true);

  const onRefreshFile = () => {
    if (reportGenerationMode === REPORT_GENERATION_MODES.PREVIEW_AND_SHARE) {
      onGenerateReport();
    } else {
      onQuickPreview();
    }
  };

  const handleHideGeneratedFile = useCallback(() => {
    setGeneratedFile(prev => {
      if (prev?.href) {
        URL.revokeObjectURL(prev.href);
      }
      return null;
    });
  }, []);

  return {
    isSaving,
    isGeneratingReport,
    reportGenerationMode,
    generatedFile,
    onSaveReport,
    onGenerateReport,
    onQuickPreview,
    onRefreshFile,
    handleHideGeneratedFile
  };
};

export default useReportGeneration;
